import { api } from './base';

/**
 * Reliability Data API - for managing reliability data sources and failure rates
 * 
 * COMPLIANCE: All failure rates used in safety analyses must trace back to
 * approved, versioned source documents via these endpoints.
 */
export interface ReliabilityDataSource {
  sourceId: string;
  sourceType: 'mil_std' | 'vendor' | 'industry_db' | 'company' | 'derived';
  sourceName: string;
  version: string;
  publicationDate: string;
  retrievalDate: string;
  approvalStatus: 'pending' | 'approved' | 'deprecated';
  approvedBy?: string;
  approvalDate?: string;
  documentReference?: string;
  notes?: string;
  createdAt: string;
  createdBy: string;
  lastModified?: string;
  modifiedBy?: string;
}

export interface FailureRateRecord {
  recordId: string;
  sourceId: string;
  partNumber?: string;
  partType: string;
  partCategory?: string;
  failureRate: number;
  failureRateUnit: 'per_hour' | 'per_million_hours' | 'FIT';
  confidenceLevel?: number;
  environmentalCategory?: string;
  temperatureRange?: { min: number; max: number };
  qualityLevel?: string;
  failureModes?: Array<{ mode: string; percentage: number; rate?: number; notes?: string }>;
  citation: {
    sourceDocument: string;
    section?: string;
    table?: string;
    equation?: string;
    pageNumber?: string;
    figure?: string;
    accessDate: string;
    verifiedBy?: string;
    verificationDate?: string;
    notes?: string;
  };
  createdAt: string;
  createdBy: string;
  lastModified?: string;
  modifiedBy?: string;
}

export const reliabilityApi = {
  // Data Sources
  listSources: async (approvalStatus?: string) => {
    const params: Record<string, string> = {};
    if (approvalStatus) params.approvalStatus = approvalStatus;
    
    const response = await api.get('/reliability/sources', { params });
    return response.data.data as ReliabilityDataSource[];
  },

  getSource: async (sourceId: string) => {
    const response = await api.get(`/reliability/sources/${encodeURIComponent(sourceId)}`);
    return response.data.data as ReliabilityDataSource;
  },

  createSource: async (source: Omit<ReliabilityDataSource, 'sourceId' | 'createdAt' | 'createdBy'>) => {
    const response = await api.post('/reliability/sources', source);
    return response.data.data as ReliabilityDataSource;
  },

  updateSource: async (sourceId: string, updates: Partial<ReliabilityDataSource>) => {
    const response = await api.patch(`/reliability/sources/${encodeURIComponent(sourceId)}`, updates);
    return response.data.data as ReliabilityDataSource;
  },

  approveSource: async (sourceId: string) => {
    const response = await api.post(`/reliability/sources/${encodeURIComponent(sourceId)}/approve`);
    return response.data.data as ReliabilityDataSource;
  },

  deprecateSource: async (sourceId: string) => {
    const response = await api.post(`/reliability/sources/${encodeURIComponent(sourceId)}/deprecate`);
    return response.data.data as ReliabilityDataSource;
  },

  // Failure Rate Records
  listRecords: async (sourceId?: string, partCategory?: string) => {
    const params: Record<string, string> = {};
    if (sourceId) params.sourceId = sourceId;
    if (partCategory) params.partCategory = partCategory;
    
    const response = await api.get('/reliability/records', { params });
    return response.data.data as FailureRateRecord[];
  },

  getRecord: async (recordId: string) => {
    const response = await api.get(`/reliability/records/${encodeURIComponent(recordId)}`);
    return response.data.data as FailureRateRecord;
  },

  createRecord: async (record: Omit<FailureRateRecord, 'recordId' | 'createdAt' | 'createdBy'>) => {
    const response = await api.post('/reliability/records', record);
    return response.data.data as FailureRateRecord;
  },

  updateRecord: async (recordId: string, updates: Partial<FailureRateRecord>) => {
    const response = await api.patch(`/reliability/records/${encodeURIComponent(recordId)}`, updates);
    return response.data.data as FailureRateRecord;
  },

  deleteRecord: async (recordId: string) => {
    await api.delete(`/reliability/records/${encodeURIComponent(recordId)}`);
  },

  // Search
  searchRecords: async (query: string, limit: number = 10) => {
    const response = await api.get('/reliability/search', { params: { query, limit } });
    return response.data.data as FailureRateRecord[];
  },
};
